/**
 * Alert Model - Phase 15
 * 
 * Normalized alert shape for reliability alerting system.
 * Alerts are workflow overlays, not source-of-truth replacements.
 * 
 * SIGNAL SOURCES MAPPED:
 * - /lib/offlineAudit/incidentStore.js: getIncidentSummary, aggregateAllIncidents
 * - /lib/offlineAudit/incidentSelectors.js: applyFilters, getCriticalIncidents
 * - /lib/reliability/reliabilitySelectors.js: getMerchantReliabilitySummary, getBlockedPaymentReliability
 * - /lib/offline/db.js: listQueueItems, listSyncLog, getQueueStats
 * - /lib/offline/network.js: getNetworkStatus
 */

import { generateUUID } from '../offline/db';

// ==================== ALERT TYPES ====================

export const ALERT_TYPES = {
  CRITICAL_OPEN_INCIDENTS: 'critical_open_incidents',
  BLOCKED_PAYMENTS_THRESHOLD: 'blocked_payments_threshold',
  STALE_PENDING_QUEUE: 'stale_pending_queue',
  REPEATED_SYNC_FAILURES: 'repeated_sync_failures',
  TERMINAL_UNSYNCED_ORDERS: 'terminal_unsynced_orders',
  LOCATION_REPEATED_ISSUES: 'location_repeated_issues',
  NO_SUCCESSFUL_SYNC_RECENTLY: 'no_successful_sync_recently',
  CONFLICT_RATE_HIGH: 'conflict_rate_high',
  RELIABILITY_STATUS_AT_RISK: 'reliability_status_at_risk',
  FAILED_KITCHEN_RECEIPT_BACKLOG: 'failed_kitchen_receipt_backlog'
};

export const SOURCE_DOMAINS = {
  SYNC: 'sync',
  INCIDENTS: 'incidents',
  PAYMENTS: 'payments',
  ORDERS: 'orders',
  KITCHEN: 'kitchen',
  RECEIPTS: 'receipts',
  STAFF: 'staff',
  BACKOFFICE: 'backoffice',
  SYSTEM: 'system'
};

export const ALERT_SEVERITY = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical'
};

export const ALERT_STATUS = {
  OPEN: 'open',
  ACKNOWLEDGED: 'acknowledged',
  ESCALATED: 'escalated',
  RESOLVED: 'resolved',
  DISMISSED: 'dismissed',
  SUPPRESSED: 'suppressed'
};

// ==================== ALERT SHAPE ====================

/**
 * @typedef {Object} Alert
 * @property {string} alertId
 * @property {string} alertType
 * @property {string} sourceDomain
 * @property {string} severity
 * @property {string} status
 * @property {string} title
 * @property {string} message
 * @property {Object} scope
 * @property {Object} relatedIds
 * @property {Object} triggeredBy
 * @property {number} createdAt
 * @property {number|null} updatedAt
 * @property {number|null} firstObservedAt
 * @property {number|null} lastObservedAt
 * @property {number|null} acknowledgedAt
 * @property {number|null} escalatedAt
 * @property {number|null} resolvedAt
 * @property {number|null} dismissedAt
 * @property {Object|null} actorState
 * @property {string|null} managerNote
 * @property {string[]} tags
 * @property {Object} metadata
 */

/**
 * Create empty alert template
 * @returns {Alert}
 */
export function createEmptyAlert() {
  const now = Date.now();
  return {
    alertId: `alert_${generateUUID()}`,
    alertType: '',
    sourceDomain: SOURCE_DOMAINS.SYSTEM,
    severity: ALERT_SEVERITY.INFO,
    status: ALERT_STATUS.OPEN,
    title: '',
    message: '',
    scope: {
      merchantId: null,
      locationId: null,
      terminalId: null
    },
    relatedIds: {
      incidentId: null,
      queueItemId: null,
      localOrderId: null,
      serverOrderId: null,
      paymentIntentId: null,
      entityId: null
    },
    triggeredBy: {
      ruleId: '',
      ruleName: '',
      observedValue: null,
      threshold: null
    },
    createdAt: now,
    updatedAt: null,
    firstObservedAt: now,
    lastObservedAt: now,
    acknowledgedAt: null,
    escalatedAt: null,
    resolvedAt: null,
    dismissedAt: null,
    actorState: {
      acknowledgedBy: null,
      escalatedBy: null,
      dismissedBy: null,
      resolvedBy: null
    },
    managerNote: null,
    tags: [],
    metadata: {}
  };
}

// ==================== ALERT BUILDERS ====================

/**
 * Build alert from rule evaluation result
 * @param {Object} options
 * @returns {Alert}
 */
export function buildAlert(options) {
  const {
    alertType,
    sourceDomain,
    severity,
    title,
    message,
    scope = {},
    relatedIds = {},
    triggeredBy = {},
    tags = [],
    metadata = {}
  } = options;

  const alert = createEmptyAlert();
  
  alert.alertType = alertType;
  alert.sourceDomain = sourceDomain || mapAlertTypeToDomain(alertType);
  alert.severity = severity || ALERT_SEVERITY.WARNING;
  alert.title = title || buildAlertTitle(alertType, options);
  alert.message = message || buildAlertMessage(alertType, options);
  alert.scope = {
    merchantId: scope.merchantId || null,
    locationId: scope.locationId || null,
    terminalId: scope.terminalId || null
  };
  alert.relatedIds = { ...alert.relatedIds, ...relatedIds };
  alert.triggeredBy = {
    ruleId: triggeredBy.ruleId || alertType,
    ruleName: triggeredBy.ruleName || getAlertTypeLabel(alertType),
    observedValue: triggeredBy.observedValue ?? null,
    threshold: triggeredBy.threshold ?? null
  };
  alert.tags = tags.length > 0 ? tags : buildAlertTags(alertType, options);
  alert.metadata = metadata;

  return alert;
}

/**
 * Build alert title from type and context
 * @param {string} alertType
 * @param {Object} options
 * @returns {string}
 */
export function buildAlertTitle(alertType, options = {}) {
  const { scope = {}, triggeredBy = {} } = options;
  const scopeLabel = buildScopeLabel(scope);
  
  const titles = {
    [ALERT_TYPES.CRITICAL_OPEN_INCIDENTS]: `Critical Incident${scopeLabel}`,
    [ALERT_TYPES.BLOCKED_PAYMENTS_THRESHOLD]: `Blocked Payments${scopeLabel}`,
    [ALERT_TYPES.STALE_PENDING_QUEUE]: `Stale Sync Queue${scopeLabel}`,
    [ALERT_TYPES.REPEATED_SYNC_FAILURES]: `Repeated Sync Failures${scopeLabel}`,
    [ALERT_TYPES.TERMINAL_UNSYNCED_ORDERS]: `Unsynced Orders${scopeLabel}`,
    [ALERT_TYPES.LOCATION_REPEATED_ISSUES]: `Location Repeated Issues${scopeLabel}`,
    [ALERT_TYPES.NO_SUCCESSFUL_SYNC_RECENTLY]: `No Recent Sync${scopeLabel}`,
    [ALERT_TYPES.CONFLICT_RATE_HIGH]: `High Conflict Rate${scopeLabel}`,
    [ALERT_TYPES.RELIABILITY_STATUS_AT_RISK]: `Reliability At Risk${scopeLabel}`,
    [ALERT_TYPES.FAILED_KITCHEN_RECEIPT_BACKLOG]: `Kitchen/Receipt Backlog${scopeLabel}`
  };
  
  return titles[alertType] || `Alert: ${alertType}`;
}

/**
 * Build alert message from type and context
 * @param {string} alertType
 * @param {Object} options
 * @returns {string}
 */
export function buildAlertMessage(alertType, options = {}) {
  const { triggeredBy = {} } = options;
  const { observedValue, threshold } = triggeredBy;
  
  const messages = {
    [ALERT_TYPES.CRITICAL_OPEN_INCIDENTS]: 
      `${observedValue || 1} critical incident(s) require immediate attention.`,
    [ALERT_TYPES.BLOCKED_PAYMENTS_THRESHOLD]: 
      `${observedValue || 0} blocked payments detected (threshold: ${threshold || 3}).`,
    [ALERT_TYPES.STALE_PENDING_QUEUE]: 
      `Oldest pending item is ${formatDuration(observedValue)} old (threshold: ${formatDuration(threshold)}).`,
    [ALERT_TYPES.REPEATED_SYNC_FAILURES]: 
      `${observedValue || 0} sync failures in recent window (threshold: ${threshold || 5}).`,
    [ALERT_TYPES.TERMINAL_UNSYNCED_ORDERS]: 
      `${observedValue || 0} unsynced orders on this terminal (threshold: ${threshold || 5}).`,
    [ALERT_TYPES.LOCATION_REPEATED_ISSUES]: 
      `${observedValue || 0} repeated issues at this location.`,
    [ALERT_TYPES.NO_SUCCESSFUL_SYNC_RECENTLY]: 
      `No successful sync in ${formatDuration(observedValue)} (threshold: ${formatDuration(threshold)}).`,
    [ALERT_TYPES.CONFLICT_RATE_HIGH]: 
      `Conflict rate is ${observedValue}% (threshold: ${threshold}%).`,
    [ALERT_TYPES.RELIABILITY_STATUS_AT_RISK]: 
      `Reliability status is ${observedValue}. Immediate review recommended.`,
    [ALERT_TYPES.FAILED_KITCHEN_RECEIPT_BACKLOG]: 
      `${observedValue || 0} failed kitchen/receipt items pending.`
  };
  
  return messages[alertType] || `Alert condition detected for ${alertType}.`;
}

/**
 * Build alert tags from type and context
 * @param {string} alertType
 * @param {Object} options
 * @returns {string[]}
 */
export function buildAlertTags(alertType, options = {}) {
  const { scope = {}, severity } = options;
  const tags = [];
  
  // Domain tag
  const domain = mapAlertTypeToDomain(alertType);
  if (domain) tags.push(domain);
  
  // Severity tag
  if (severity) tags.push(severity);
  
  // Scope tags
  if (scope.terminalId) tags.push('terminal');
  if (scope.locationId) tags.push('location');
  
  // Type-specific tags
  if (alertType === ALERT_TYPES.BLOCKED_PAYMENTS_THRESHOLD) tags.push('revenue-impact');
  if (alertType === ALERT_TYPES.CRITICAL_OPEN_INCIDENTS) tags.push('urgent');
  
  return tags;
}

/**
 * Normalize rule result to alert
 * @param {Object} ruleResult
 * @returns {Alert}
 */
export function normalizeRuleResultToAlert(ruleResult) {
  return buildAlert({
    alertType: ruleResult.ruleId,
    severity: ruleResult.severity,
    scope: ruleResult.scope,
    relatedIds: ruleResult.relatedIds,
    triggeredBy: {
      ruleId: ruleResult.ruleId,
      ruleName: ruleResult.ruleName,
      observedValue: ruleResult.observedValue,
      threshold: ruleResult.threshold
    },
    tags: ruleResult.tags,
    metadata: ruleResult.metadata
  });
}

// ==================== HELPERS ====================

/**
 * Map alert type to source domain
 * @param {string} alertType
 * @returns {string}
 */
function mapAlertTypeToDomain(alertType) {
  const mapping = {
    [ALERT_TYPES.CRITICAL_OPEN_INCIDENTS]: SOURCE_DOMAINS.INCIDENTS,
    [ALERT_TYPES.BLOCKED_PAYMENTS_THRESHOLD]: SOURCE_DOMAINS.PAYMENTS,
    [ALERT_TYPES.STALE_PENDING_QUEUE]: SOURCE_DOMAINS.SYNC,
    [ALERT_TYPES.REPEATED_SYNC_FAILURES]: SOURCE_DOMAINS.SYNC,
    [ALERT_TYPES.TERMINAL_UNSYNCED_ORDERS]: SOURCE_DOMAINS.ORDERS,
    [ALERT_TYPES.LOCATION_REPEATED_ISSUES]: SOURCE_DOMAINS.INCIDENTS,
    [ALERT_TYPES.NO_SUCCESSFUL_SYNC_RECENTLY]: SOURCE_DOMAINS.SYNC,
    [ALERT_TYPES.CONFLICT_RATE_HIGH]: SOURCE_DOMAINS.SYNC,
    [ALERT_TYPES.RELIABILITY_STATUS_AT_RISK]: SOURCE_DOMAINS.SYSTEM,
    [ALERT_TYPES.FAILED_KITCHEN_RECEIPT_BACKLOG]: SOURCE_DOMAINS.KITCHEN
  };
  return mapping[alertType] || SOURCE_DOMAINS.SYSTEM;
}

/**
 * Get human-readable label for alert type
 * @param {string} alertType
 * @returns {string}
 */
export function getAlertTypeLabel(alertType) {
  const labels = {
    [ALERT_TYPES.CRITICAL_OPEN_INCIDENTS]: 'Critical Open Incidents',
    [ALERT_TYPES.BLOCKED_PAYMENTS_THRESHOLD]: 'Blocked Payments Threshold',
    [ALERT_TYPES.STALE_PENDING_QUEUE]: 'Stale Pending Queue',
    [ALERT_TYPES.REPEATED_SYNC_FAILURES]: 'Repeated Sync Failures',
    [ALERT_TYPES.TERMINAL_UNSYNCED_ORDERS]: 'Terminal Unsynced Orders',
    [ALERT_TYPES.LOCATION_REPEATED_ISSUES]: 'Location Repeated Issues',
    [ALERT_TYPES.NO_SUCCESSFUL_SYNC_RECENTLY]: 'No Successful Sync Recently',
    [ALERT_TYPES.CONFLICT_RATE_HIGH]: 'High Conflict Rate',
    [ALERT_TYPES.RELIABILITY_STATUS_AT_RISK]: 'Reliability Status At Risk',
    [ALERT_TYPES.FAILED_KITCHEN_RECEIPT_BACKLOG]: 'Failed Kitchen/Receipt Backlog'
  };
  return labels[alertType] || alertType;
}

/**
 * Build scope label for title
 * @param {Object} scope
 * @returns {string}
 */
function buildScopeLabel(scope) {
  if (scope.terminalId) return ` (Terminal ${scope.terminalId})`;
  if (scope.locationId) return ` (Location ${scope.locationId})`;
  if (scope.merchantId) return ` (Merchant ${scope.merchantId})`;
  return '';
}

/**
 * Format duration in ms to human readable
 * @param {number} ms
 * @returns {string}
 */
function formatDuration(ms) {
  if (!ms) return 'N/A';
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
  if (ms < 86400000) return `${(ms / 3600000).toFixed(1)}h`;
  return `${(ms / 86400000).toFixed(1)}d`;
}

/**
 * Check if alert is open (needs attention)
 * @param {Alert} alert
 * @returns {boolean}
 */
export function isAlertOpen(alert) {
  return [ALERT_STATUS.OPEN, ALERT_STATUS.ACKNOWLEDGED, ALERT_STATUS.ESCALATED].includes(alert.status);
}

/**
 * Check if alert is actionable
 * @param {Alert} alert
 * @returns {boolean}
 */
export function isAlertActionable(alert) {
  return alert.status !== ALERT_STATUS.RESOLVED && alert.status !== ALERT_STATUS.DISMISSED;
}

export default {
  ALERT_TYPES,
  SOURCE_DOMAINS,
  ALERT_SEVERITY,
  ALERT_STATUS,
  createEmptyAlert,
  buildAlert,
  buildAlertTitle,
  buildAlertMessage,
  buildAlertTags,
  normalizeRuleResultToAlert,
  getAlertTypeLabel,
  isAlertOpen,
  isAlertActionable
};
